/**
 * TemplatePreviewModal - Template Preview and Confirmation Modal
 * Shows detailed template information before creation
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Project } from '@fmeastudio/shared';
import { getProjectStats, type ProjectStats } from '../../api/projects';
import { useTerminology } from '../../hooks/useTerminology';
import './TemplatePreviewModal.css';

interface TemplatePreviewModalProps {
  template: Project;
  onConfirm: () => void;
  onCancel: () => void;
}

export function TemplatePreviewModal({ template, onConfirm, onCancel }: TemplatePreviewModalProps) {
  const { t } = useTerminology();
  const { data: stats, isLoading } = useQuery<ProjectStats>({
    queryKey: ['template-stats', template.id],
    queryFn: () => getProjectStats(template.id),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div
      className="template-preview-modal-overlay"
      onClick={onCancel}
      data-testid="template-preview-modal"
    >
      <div className="template-preview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="template-preview-header">
          <h2>{template.name}</h2>
          <button className="template-preview-close" onClick={onCancel} aria-label="Close preview">
            ×
          </button>
        </div>

        <div className="template-preview-body">
          {/* Template metadata */}
          <section className="template-preview-section">
            <h3>Details</h3>
            <div className="template-preview-details">
              {template.industry && (
                <div className="template-preview-detail">
                  <span className="detail-label">Industry:</span>
                  <span className="detail-value">{template.industry}</span>
                </div>
              )}
              <div className="template-preview-detail">
                <span className="detail-label">Created:</span>
                <span className="detail-value">{formatDate(template.createdAt)}</span>
              </div>
              {template.description && (
                <div className="template-preview-detail">
                  <span className="detail-label">Description:</span>
                  <p className="detail-value">{template.description}</p>
                </div>
              )}
            </div>
          </section>

          {/* Template statistics */}
          <section className="template-preview-section">
            <h3>Template Contents</h3>
            {isLoading ? (
              <div className="template-preview-loading">
                <div className="spinner-small" />
                <span>Loading template details...</span>
              </div>
            ) : stats ? (
              <div className="template-preview-stats">
                <div className="stat-card">
                  <div className="stat-value">{stats.componentCount}</div>
                  <div className="stat-label">Components</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{stats.failureModeCount}</div>
                  <div className="stat-label">{t.failureModes}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{stats.causeCount}</div>
                  <div className="stat-label">{t.rootCauses}</div>
                </div>
              </div>
            ) : (
              <p className="template-preview-error">Could not load template statistics</p>
            )}
          </section>

          {/* What will be copied */}
          <section className="template-preview-section">
            <h3>What will be included?</h3>
            <ul className="template-preview-includes">
              <li>✓ All component hierarchies</li>
              <li>✓ {t.failureModes} and effects</li>
              <li>✓ {t.rootCauses} and relationships</li>
              <li>✓ {t.rpn} values and risk assessments</li>
              <li>✓ {t.mitigation} actions (templates)</li>
            </ul>
          </section>
        </div>

        <div className="template-preview-footer">
          <button className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn-primary" onClick={onConfirm}>
            Use This Template
          </button>
        </div>
      </div>
    </div>
  );
}
